import { useEffect, useRef } from 'react';
import { useBetSlip } from './BetSlipContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function calcAdjustedSpread(baseSpread: number, baseProb: number, adj: number) {
  const newSpread = baseSpread + adj;
  const spreadStr = newSpread >= 0 ? `+${newSpread.toFixed(1)}` : `${newSpread.toFixed(1)}`;
  const probDelta = adj * 0.025 * (baseSpread >= 0 ? 1 : -1);
  const newProb = Math.min(0.95, Math.max(0.05, baseProb + probDelta));
  const payout = 1 / newProb;
  const profit = payout - 1;
  return {
    spreadStr,
    payoutStr: `$${payout.toFixed(2)}`,
    profitStr: `+$${profit.toFixed(2)}`,
    profitNum: profit,
    payoutNum: payout,
  };
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export default function BetSlipPanel() {
  const { items, isOpen, spreadAdjs, closeSlip, removeBet, updateQty, updateSpreadAdj, clearAll } = useBetSlip();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        closeSlip();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closeSlip]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const totalStake = items.reduce((acc, i) => acc + i.qty, 0);

  const totalPayout = items.reduce((acc, i) => {
    if (i.isSpread && i.baseSpread !== undefined && i.baseProb !== undefined) {
      const adj = spreadAdjs[i.id] ?? 0;
      const { payoutNum } = calcAdjustedSpread(i.baseSpread, i.baseProb, adj);
      return acc + payoutNum * i.qty;
    }
    const p = parseFloat(i.payout.replace('$', '')) || 0;
    return acc + p * i.qty;
  }, 0);

  const totalProfit = items.reduce((acc, i) => {
    if (i.isSpread && i.baseSpread !== undefined && i.baseProb !== undefined) {
      const adj = spreadAdjs[i.id] ?? 0;
      const { profitNum } = calcAdjustedSpread(i.baseSpread, i.baseProb, adj);
      return acc + profitNum * i.qty;
    }
    const raw = i.profit.replace('+$', '').replace('-$', '').replace('$', '');
    const sign = i.profit.startsWith('-') ? -1 : 1;
    return acc + sign * (parseFloat(raw) || 0) * i.qty;
  }, 0);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 h-full z-[201] w-full max-w-[400px] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ background: '#111827' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60 flex-shrink-0"
          style={{ background: '#0f1623' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-500/20 border border-teal-500/30">
              <i className="ri-file-list-3-line text-teal-400 text-base"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">Bet Slip</h2>
              <p className="text-slate-500 text-xs">{items.length} selection{items.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-slate-500 hover:text-rose-400 transition-colors cursor-pointer px-2 py-1 rounded border border-slate-700 hover:border-rose-500/50 whitespace-nowrap"
              >
                Clear All
              </button>
            )}
            <button
              onClick={closeSlip}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer border border-slate-700"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-slate-800/60 border border-slate-700/50 mb-5">
                <i className="ri-file-list-3-line text-4xl text-slate-600"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Your slip is empty</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Click <strong className="text-teal-400">"Add to Slip"</strong> on any market to start building your bets.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {items.map((item) => (
                <BetSlipCard
                  key={item.id}
                  item={item}
                  spreadAdj={spreadAdjs[item.id] ?? 0}
                  onRemove={() => removeBet(item.id)}
                  onQtyChange={(q) => updateQty(item.id, q)}
                  onSpreadAdjChange={(adj) => updateSpreadAdj(item.id, adj)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer Summary */}
        {items.length > 0 && (
          <div
            className="flex-shrink-0 border-t border-slate-700/60 p-5 space-y-4"
            style={{ background: '#0f1623' }}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Total Selections</span>
                <span className="text-white font-semibold">{items.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Total Shares</span>
                <span className="text-white font-semibold">{totalStake}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-700/50">
                <span className="text-slate-400">Est. Payout</span>
                <span className="text-teal-400 font-bold text-base">${totalPayout.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Est. Profit</span>
                <span className={`font-bold text-base ${totalProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)}
                </span>
              </div>
            </div>

            <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-900 font-extrabold rounded-xl shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide">
              <i className="ri-check-double-line text-base"></i>
              Place {items.length} Bet{items.length !== 1 ? 's' : ''}
            </button>

            <p className="text-center text-slate-600 text-xs">
              Demo only — no real money involved
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Individual Bet Card ──────────────────────────────────────────────────────

function BetSlipCard({
  item,
  spreadAdj,
  onRemove,
  onQtyChange,
  onSpreadAdjChange,
}: {
  item: import('./BetSlipContext').BetSlipItem;
  spreadAdj: number;
  onRemove: () => void;
  onQtyChange: (qty: number) => void;
  onSpreadAdjChange: (adj: number) => void;
}) {
  const showAdjuster = item.isSpread && item.baseSpread !== undefined && item.baseProb !== undefined;

  // Compute live values when adjuster is active
  const liveValues = showAdjuster
    ? calcAdjustedSpread(item.baseSpread!, item.baseProb!, spreadAdj)
    : null;

  const displayPayout = liveValues ? liveValues.payoutStr : item.payout;
  const displayProfit = liveValues ? liveValues.profitStr : item.profit;
  const displaySpread = liveValues ? liveValues.spreadStr : null;

  const adjSteps = Math.round(spreadAdj / 0.5);
  const minSteps = -6;
  const maxSteps = 6;

  return (
    <div
      className="rounded-xl border border-slate-700/60 overflow-hidden"
      style={{ background: '#1a2235' }}
    >
      {/* Top row */}
      <div className="flex items-start justify-between px-3.5 pt-3 pb-2">
        <div className="flex-1 min-w-0 pr-2">
          {item.badge && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5 border ${item.badgeColor || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
              {item.badge}
            </span>
          )}
          <div className="text-[11px] text-slate-500 truncate mb-0.5">{item.market}</div>
          <div className="text-xs text-slate-400 truncate mb-1">{item.matchup}</div>
          <div className="text-sm font-bold text-teal-300 truncate">
            {item.pick}
            {displaySpread && spreadAdj !== 0 && (
              <span className="ml-1.5 text-xs font-semibold text-amber-400">({displaySpread})</span>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          className="w-6 h-6 flex items-center justify-center rounded text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {/* Odds row */}
      <div className="px-3.5 pb-2 flex items-center gap-3 text-xs">
        <span className="text-slate-500">Price: <span className="text-slate-300 font-medium">{item.price}</span></span>
        <span className="text-slate-600">•</span>
        <span className="text-slate-500">Payout: <span className="text-slate-300 font-medium">{displayPayout}</span></span>
        <span className={`font-bold ml-auto ${displayProfit.startsWith('+') ? 'text-teal-400' : 'text-rose-400'}`}>
          {displayProfit}
        </span>
      </div>

      {/* ── Spread Adjuster (only for spread bets) ── */}
      {showAdjuster && (
        <div className="px-3.5 pb-2.5">
          <div className="bg-slate-800/60 rounded-lg border border-slate-700/60 px-3 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1">
                <i className="ri-equalizer-line text-teal-400 text-xs"></i>
                Adjust Spread
              </span>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                spreadAdj === 0
                  ? 'bg-slate-700 text-slate-400'
                  : spreadAdj > 0
                    ? 'bg-teal-500/20 text-teal-400'
                    : 'bg-rose-500/20 text-rose-400'
              }`}>
                {spreadAdj === 0 ? 'Market' : spreadAdj > 0 ? `+${spreadAdj.toFixed(1)}` : `${spreadAdj.toFixed(1)}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onSpreadAdjChange(Math.max(minSteps * 0.5, spreadAdj - 0.5))}
                disabled={adjSteps <= minSteps}
                className="w-5 h-5 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-600 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-subtract-line text-xs"></i>
              </button>
              <div className="flex-1">
                <input
                  type="range"
                  min={minSteps}
                  max={maxSteps}
                  step={1}
                  value={adjSteps}
                  onChange={e => onSpreadAdjChange(parseInt(e.target.value) * 0.5)}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((adjSteps - minSteps) / (maxSteps - minSteps)) * 100}%, #334155 ${((adjSteps - minSteps) / (maxSteps - minSteps)) * 100}%, #334155 100%)`,
                    accentColor: '#14b8a6',
                  }}
                />
              </div>
              <button
                onClick={() => onSpreadAdjChange(Math.min(maxSteps * 0.5, spreadAdj + 0.5))}
                disabled={adjSteps >= maxSteps}
                className="w-5 h-5 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-600 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-add-line text-xs"></i>
              </button>
            </div>
            <div className="flex justify-between mt-0.5">
              <span className="text-[9px] text-slate-600">-3.0</span>
              <span className="text-[9px] text-slate-600">0</span>
              <span className="text-[9px] text-slate-600">+3.0</span>
            </div>
          </div>
        </div>
      )}

      {/* Qty row */}
      <div
        className="px-3.5 py-2.5 border-t border-slate-700/50 flex items-center gap-3"
        style={{ background: '#131c2c' }}
      >
        <span className="text-xs text-slate-500">Shares:</span>
        <div className="flex items-center border border-slate-700 rounded-lg overflow-hidden bg-slate-800/60">
          <button
            onClick={() => onQtyChange(item.qty - 1)}
            className="px-2.5 py-1.5 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors cursor-pointer text-sm font-bold"
          >
            −
          </button>
          <span className="px-3 py-1.5 text-sm font-bold text-white min-w-[2rem] text-center">{item.qty}</span>
          <button
            onClick={() => onQtyChange(item.qty + 1)}
            className="px-2.5 py-1.5 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors cursor-pointer text-sm font-bold"
          >
            +
          </button>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-slate-500">Total payout</div>
          <div className="text-sm font-bold text-teal-400">
            ${(parseFloat(displayPayout.replace('$', '')) * item.qty).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}
